import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Generic click-outside-to-close menu. `trigger` receives {open, toggle}.
// The menu is portaled to document.body and positioned via the trigger's
// getBoundingClientRect() — it can't be a normal absolutely-positioned child of the
// trigger, because callers (e.g. DataTable's row actions) live inside an
// `overflow-x-auto` container. Setting overflow-x to anything but visible forces
// overflow-y to clip too (per the CSS spec, even though only x was set), so a menu
// positioned in-flow gets silently cut off near the bottom/right of the table —
// no z-index fixes that, since it's a clipping issue, not a stacking one.
export default function Dropdown({ trigger, children, align = 'right', className = '' }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updateCoords = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({
      top: rect.bottom + window.scrollY + 8,
      left: align === 'right' ? rect.right + window.scrollX : rect.left + window.scrollX,
      align,
    });
  };

  useLayoutEffect(() => {
    if (open) updateCoords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onReposition = () => updateCoords();
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="inline-block" ref={triggerRef}>
      {trigger({ open, toggle: () => setOpen((o) => !o), close: () => setOpen(false) })}
      {open && coords && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'absolute',
            top: coords.top,
            left: coords.align === 'right' ? undefined : coords.left,
            right: coords.align === 'right' ? window.innerWidth - coords.left : undefined,
          }}
          className={cx(
            'min-w-[12rem] bg-white rounded-xl shadow-lg border border-neutral-100 py-1 z-50 ring-1 ring-black/5 dark:bg-ink-900 dark:border-neutral-700 dark:ring-white/10',
            className
          )}
        >
          {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
        </div>,
        document.body
      )}
    </div>
  );
}

export function DropdownItem({ icon: Icon, children, className = '', ...rest }) {
  return (
    <button
      className={cx(
        'w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50 text-left dark:text-neutral-300 dark:hover:bg-ink-800',
        className
      )}
      {...rest}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}
